import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { entryHasAnyScoping } from '@/lib/permissions/grant-scoping';

export const ADMIN_PERMISSION_GROUP = 'ahoy_schema_admin';

/**
 * Standalone utility function to check if a permission group exists in a list.
 * @param permissionGroups - Array of permission group names
 * @param groupName - Name of the permission group to check for
 * @returns true if the group exists in the array, false otherwise
 */
export function hasPermissionGroup(
  permissionGroups: string[] | undefined,
  groupName: string
): boolean {
  return permissionGroups?.includes(groupName) ?? false;
}

/**
 * Standalone utility function to check if an entity permission exists in a list.
 * Useful outside the hook context (e.g., in FullPageOrgSwitcher which iterates all orgs).
 * @param permissions - Array of permission objects with entityTypeName and eventTypeName
 * @param entityTypeName - The entity type to check for
 * @param eventTypeName - Optional event type to check for. If omitted or 'read', checks entity access only.
 * @returns true if the permission exists, false otherwise
 */
export function hasEntityPermission(
  permissions:
    | Array<{
        entityTypeName?: string;
        eventTypeName?: string;
        scopingAssociationTypeName?: string;
        entity_type_name?: string;
        event_type_name?: string;
        scoping_association_type_name?: string;
      }>
    | undefined,
  entityTypeName: string,
  eventTypeName?: string
): boolean {
  if (!permissions) {
    return false;
  }
  // The API returns these items with snake_case keys (see TeamSerializer ->
  // permissions field, which is declared as `additionalProperties: {}` in the
  // OpenAPI schema, so the generated TeamFromJSON pass-through doesn't
  // camelCase them). Existing test mocks use camelCase. Read both so the
  // hook works against real API responses and against the test fixtures.
  // Property-scoped grants additionally carry `scopingPropertyTypeName`/
  // `scopingPropertyValue` (or their snake_case forms) — present only on
  // entries that scope to a property value rather than an association.
  const getEntity = (
    p: NonNullable<typeof permissions>[number]
  ): string | undefined => p.entityTypeName ?? p.entity_type_name;
  const getEvent = (
    p: NonNullable<typeof permissions>[number]
  ): string | undefined => p.eventTypeName ?? p.event_type_name;
  if (!eventTypeName || eventTypeName === 'read') {
    return permissions.some(p => getEntity(p) === entityTypeName);
  }
  return permissions.some(
    p => getEntity(p) === entityTypeName && getEvent(p) === eventTypeName
  );
}

/**
 * Standalone utility function to check if a type's read access is scoped (has
 * at least one scoped read entry and no unrestricted read entry).
 * An unrestricted read subsumes all scoped reads, so when both exist the type
 * is NOT considered scoped.
 */
export function hasScopedRead(
  permissions: Array<Record<string, unknown>> | undefined,
  entityTypeName: string
): boolean {
  if (!permissions) {
    return false;
  }
  const reads = permissions.filter(p => {
    const entity = (p.entityTypeName ?? p.entity_type_name) as
      | string
      | undefined;
    const event = (p.eventTypeName ?? p.event_type_name) as string | undefined;
    return entity === entityTypeName && (!event || event === 'read');
  });
  const hasScoped = reads.some(r => entryHasAnyScoping(r));
  const hasUnrestricted = reads.some(r => !entryHasAnyScoping(r));
  return hasScoped && !hasUnrestricted;
}

/**
 * Hook to access current user's permission groups and entity permissions.
 *
 * The returned object and its closures are memoized on the underlying
 * `currentOrg` permission data (stable references — `currentOrg` is
 * useState-backed), so `hasPermission` / `hasPermissionGroup` are safe to use
 * in `useMemo`/`useCallback`/`useEffect` dependency arrays and as props.
 * @returns Object with permissionGroups, hasPermissionGroup, isSchemaAdmin, and hasPermission
 */
export function usePermissions() {
  // Call hook unconditionally at top level per React rules-of-hooks
  const { currentOrg } = useOrg();
  const permissionGroups = currentOrg?.permissionGroups;
  const permissions = currentOrg?.permissions as
    | Array<{
        entityTypeName?: string;
        eventTypeName?: string;
        scopingAssociationTypeName?: string;
        entity_type_name?: string;
        event_type_name?: string;
        scoping_association_type_name?: string;
        scopingPropertyTypeName?: string;
        scopingPropertyValue?: string;
        scoping_property_type_name?: string;
        scoping_property_value?: string;
      }>
    | undefined;

  return useMemo(
    () => ({
      permissionGroups: permissionGroups ?? [],
      hasPermissionGroup: (group: string) =>
        hasPermissionGroup(permissionGroups, group),
      isSchemaAdmin:
        permissionGroups?.includes(ADMIN_PERMISSION_GROUP) ?? false,
      hasPermission: (entityTypeName: string, eventTypeName?: string) =>
        hasEntityPermission(permissions, entityTypeName, eventTypeName),
      hasScopedRead: (entityTypeName: string) =>
        hasScopedRead(
          permissions as Array<Record<string, unknown>> | undefined,
          entityTypeName
        ),
    }),
    [permissionGroups, permissions]
  );
}

/**
 * Canonical residual-affordance write gate: does this member belong to the
 * default write permission group? Single source of truth for the
 * `hasPermissionGroup('ahoy_default')` check — see the viewer-seats client
 * issue's Decision on residual affordances for why `'ahoy_default'` is the
 * gate that separates viewers from write-capable members.
 *
 * Use this only for already-shipped residual-affordance sites. For new feature
 * surfaces, use `useFeatureAccess` instead.
 * @returns true if the current member can write, false otherwise
 */
export function useCanWrite(): boolean {
  return usePermissions().hasPermissionGroup('ahoy_default');
}

export type FeatureName = 'ai_actions' | 'chat' | 'crm_export';

/**
 * Server-resolved UI feature signal for seat-gated surfaces.
 *
 * features are a UI hint only — never authorization; every gate re-derives
 * server-side. Do NOT read memberKind for gating anywhere.
 *
 * Missing features field (stale payload) → all features present; the server
 * re-derives on every gated request regardless. This ensures a stale payload
 * never paywalls a full seat.
 *
 * Use this hook for feature-gated surfaces (e.g. FEATURE_CHAT, FEATURE_CRM_EXPORT).
 * Entity affordances use hasPermission(entityType, event).
 * useCanWrite is for already-shipped residual-affordance sites only.
 *
 * @returns Object with hasFeature (checks feature inclusion) and features (raw list or undefined)
 */
export function useFeatureAccess(): {
  hasFeature: (name: FeatureName) => boolean;
  features: string[] | undefined;
} {
  const { currentOrg } = useOrg();
  const features = currentOrg?.features;

  return useMemo(
    () => ({
      hasFeature: (name: FeatureName) =>
        features === undefined || features.includes(name),
      features,
    }),
    [features]
  );
}
